import { useVirtualizer } from '@tanstack/react-virtual';
import type { VirtualItem } from '@tanstack/react-virtual';
import type { Row, ColumnPinningState } from '@tanstack/react-table';
import { useCallback, useEffect, useRef } from 'react';
import type { EventState } from 'features/misc/table';

export type VirtualRowItem<T> = Row<T> | { isExpandedRow: boolean; parentRow: Row<T> };

export type VirtualSnapshot = {
  virtualItems: VirtualItem[];
  totalSize: number;
};

/**
 * Virtualization hook that returns an atomic snapshot { virtualItems, totalSize }
 * read directly from the virtualizer during render (no rAF delay).
 * Uses refs for stable callbacks to prevent unnecessary virtualizer reconfigurations.
 */
export const useVirtual = <T,>(
  rows: VirtualRowItem<T>[],
  container: React.MutableRefObject<HTMLDivElement | null>,
  disabled = false,
  rowHeight: number,
  expandedRowHeight?: number | ((row?: Row<T>) => number),
  overscan = 20
): VirtualSnapshot => {
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const rowHeightRef = useRef(rowHeight);
  rowHeightRef.current = rowHeight;

  const expandedRowHeightRef = useRef(expandedRowHeight);
  expandedRowHeightRef.current = expandedRowHeight;

  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const getScrollElement = useCallback(() => container.current, [container]);

  const estimateSize = useCallback((index: number) => {
    const row = rowsRef.current[index];
    if (row && typeof row === 'object' && 'isExpandedRow' in row) {
      const erh = expandedRowHeightRef.current;
      if (typeof erh === 'function') {
        return erh(row?.parentRow);
      }
      return erh ?? rowHeightRef.current;
    }
    return rowHeightRef.current;
  }, []);

  const virtualizer = useVirtualizer({
    getScrollElement,
    count: rows.length,
    estimateSize,
    overscan,
  });

  const prevRowHeight = useRef(rowHeight);
  const rowsMeasurementKey = rows
    .map(row => {
      if (typeof row === 'object' && 'isExpandedRow' in row) {
        const height =
          typeof expandedRowHeight === 'function'
            ? expandedRowHeight(row.parentRow)
            : expandedRowHeight ?? rowHeight;
        return `${row.parentRow.id}-expanded-${height}`;
      }
      return row.id;
    })
    .join('|');
  const prevRowsMeasurementKey = useRef(rowsMeasurementKey);
  useEffect(() => {
    if (
      prevRowHeight.current !== rowHeight ||
      prevRowsMeasurementKey.current !== rowsMeasurementKey
    ) {
      virtualizer.measure();
      prevRowHeight.current = rowHeight;
      prevRowsMeasurementKey.current = rowsMeasurementKey;
    }
  }, [rowHeight, rowsMeasurementKey, virtualizer]);

  // Read directly from virtualizer during render — no frame delay
  let virtualItems: VirtualItem[];
  let totalSize: number;

  if (disabled) {
    const currentRows = rowsRef.current;
    const rh = rowHeightRef.current;
    const erh = expandedRowHeightRef.current;
    let currentStart = 0;
    virtualItems = currentRows.map((row, index) => {
      const isExpandedRow = typeof row === 'object' && 'isExpandedRow' in row;
      const itemHeight = isExpandedRow
        ? typeof erh === 'function'
          ? erh(row?.parentRow)
          : erh ?? rh
        : rh;
      const item = {
        key: isExpandedRow ? `${row.parentRow.id}-expanded` : row.id,
        index,
        start: currentStart,
        end: currentStart + itemHeight,
        size: itemHeight,
        lane: 0,
      };
      currentStart += itemHeight;
      return item;
    });
    totalSize = currentStart;
  } else {
    virtualItems = virtualizer.getVirtualItems();
    totalSize = virtualizer.getTotalSize();
  }

  // Empty-window guard: keep previous snapshot if virtualizer briefly returns empty
  const prevSnapshotRef = useRef<VirtualSnapshot>({ virtualItems: [], totalSize: 0 });

  if (
    virtualItems.length === 0 &&
    prevSnapshotRef.current.virtualItems.length > 0 &&
    rows.length > 0
  ) {
    return prevSnapshotRef.current;
  }

  const snapshot = { virtualItems, totalSize };
  prevSnapshotRef.current = snapshot;
  return snapshot;
};

export const useTableEvents = (
  columnVisibility: Record<string, boolean>,
  columnOrder: string[],
  columnPinning: ColumnPinningState,
  columnSizing: Record<string, number>,
  rowSelection: Record<string, boolean>,
  onEvent?: (name: string, state: EventState) => void
) => {
  const state = useRef({
    columnVisibility: '',
    columnOrder: '',
    columnPinning: '',
    columnSizing: '',
    rowSelection: '',
  });
  const handleEvent = (
    name: 'columnVisibility' | 'columnOrder' | 'columnPinning' | 'columnSizing' | 'rowSelection',
    eventState: EventState
  ) => {
    if (!onEvent) return;
    if (!state.current[name]) {
      state.current[name] = JSON.stringify(eventState);
      return;
    }
    if (state.current[name] === JSON.stringify(eventState)) return;
    onEvent(name, eventState);
    state.current[name] = JSON.stringify(eventState);
  };
  useEffect(() => {
    handleEvent('columnVisibility', columnVisibility);
  }, [columnVisibility]);
  useEffect(() => {
    handleEvent('columnOrder', columnOrder);
  }, [columnOrder]);
  useEffect(() => {
    handleEvent('columnPinning', columnPinning?.left || []);
  }, [columnPinning]);
  useEffect(() => {
    handleEvent('columnSizing', columnSizing);
  }, [columnSizing]);
  useEffect(() => {
    handleEvent('rowSelection', rowSelection);
  }, [rowSelection]);
};
